import React from "react";
import {
  FaChild,
  FaBookReader,
  FaMicroscope,
  FaGraduationCap,
  FaBookOpen,
  FaArrowRight,
} from "react-icons/fa";

const ProgramsSection = () => {
  const programs = [
    {
      title: "Pre-Primary",
      subtitle: "LKG & UKG",
      desc: "Montessori-based learning with sensory activities and play",
      age: "3-5 Years",
      icon: <FaChild className="w-6 h-6" />,
      subjects: ["Playway", "Phonics", "Art", "Music"],
      stats: ["Play-based", "Sensory", "Social Skills"],
      gradient: "bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100",
      accent: "from-pink-400 to-rose-500",
      border: "border-pink-200",
    },
    {
      title: "Primary",
      subtitle: "Grade I - V",
      desc: "Foundation building with activity-based learning approach",
      age: "6-10 Years",
      icon: <FaBookReader className="w-6 h-6" />,
      subjects: ["Science", "Maths", "SST", "Computer", 'Language'],
      stats: ["Core Subjects", "Values", "Creativity"],
      gradient: "bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100",
      accent: "from-blue-400 to-cyan-500",
      border: "border-blue-200",
    },
    {
      title: "Middle School",
      subtitle: "Grade VI - VIII",
      desc: "Subject specialization with project-based learning",
      age: "11-13 Years",
      icon: <FaMicroscope className="w-6 h-6" />,
      subjects: ["Science", "Maths", "SST", "IT", "Languages"],
      stats: ["Specialization", "Projects", "Labs"],
      gradient: "bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100",
      accent: "from-emerald-400 to-teal-500",
      border: "border-emerald-200",
    },
    {
      title: "Secondary",
      subtitle: "Grade IX - X",
      desc: "Board preparation with career guidance",
      age: "14-16 Years",
      icon: <FaGraduationCap className="w-6 h-6" />,
      subjects: ["All Streams", "Lab Work", "Projects"],
      stats: ["Board Prep", "Career", "Exams"],
      gradient: "bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100",
      accent: "from-amber-400 to-orange-500",
      border: "border-amber-200",
    },
  ];

  return (
    <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-purple-600 rounded-full bg-gradient-to-r from-purple-50 to-violet-100">
            <FaBookOpen className="w-3 h-3 mr-1.5" />
            Academic Programs
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Structured Learning Journey
          </h2>
          <p className="text-xs text-gray-600">
            Age-appropriate curriculum designed for holistic development
          </p>
        </div>

        {/* Program Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {programs.map((program, index) => {
            return (
              <div
                key={index}
                className={`group relative p-5 rounded-2xl ${program.gradient} ${program.border} border transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
              >
                {/* Age Badge */}
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 text-xs font-medium text-gray-700 bg-white/80 backdrop-blur-sm rounded-full border ${program.border} shadow-sm`}>
                    {program.age}
                  </span>
                </div>

                {/* Icon with Gradient Background */}
                <div className="mb-4">
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${program.accent} shadow-sm`}>
                    <div className="text-white">
                      {program.icon}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="mb-1 text-base font-bold text-gray-900">
                  {program.title}
                  <span className="block text-xs font-normal text-gray-500">
                    {program.subtitle}
                  </span>
                </h3>

                {/* Description */}
                <p className="mb-4 text-xs leading-relaxed text-gray-600">
                  {program.desc}
                </p>

                {/* Subjects */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1.5">
                    {program.subjects.map((subject, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 text-xs font-medium text-gray-700 bg-white/80 backdrop-blur-sm rounded-full border ${program.border}`}
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats with Gradient Dots */}
                <div className="pt-3 border-t border-gray-200/50">
                  <div className="flex items-center justify-between">
                    {program.stats.map((stat, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${program.accent} mb-1`} />
                        <span className="text-xs text-gray-600">{stat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hover Arrow */}
                <div className="absolute transition-opacity duration-300 opacity-0 bottom-3 right-3 group-hover:opacity-100">
                  <div className={`p-1.5 rounded-full bg-gradient-to-br ${program.accent} shadow-sm`}>
                    <FaArrowRight className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>

                {/* Corner Gradient Accent */}
                <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl ${program.accent} rounded-bl-2xl opacity-5`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;